import React, { useState, useMemo, useEffect } from 'react';
import cn from 'classnames';
import NotesIcon from '@assets/images/NotesIcon';
import { ReactComponent as ModifierNoteLogo } from '@assets/images/modifierNote.svg';
import { ReactComponent as ModifierMinus } from '@assets/images/modifierMinus.svg';
import { ReactComponent as ModifierPlus } from '@assets/images/modifierPlus.svg';
import { ReactComponent as ModifierDelete } from '@assets/images/modifierDelete.svg';
import { ReactComponent as ExpandMore } from '@assets/images/expand_more.svg';
import {
  TouchableButton,
  TouchableDiv,
} from '@components/common/Touchable/index';
import { LABEL } from '@veriables';
import { onCountModifer, getPath } from '../OrderItemDetailHandler';
import getDeviceType from '@utility/deviceType';
import styles from './OrderItemModifierCart.module.scss';

const { isKiosk, isTableTop } = getDeviceType();

const ModifierRowItem = React.memo(({
  item,
  level,
  activeLevel,
  items,
  levelGroupTotals,
  levelGroupWeightTotals,
  modifiersHashTable,
  modifierGroupsHashTable,
  auth,
  serverAppTheme,
  menusList,
  menuIndex,
  modifierLevelData,
  isNestedModifier,
  isGlobalModFuncDisplay,
  modifierMemosEnabled,
  noteText,
  nestedModifierArray,
  pushModifierToCount,
  onModifierEdit,
  onNoteOpen,
  handleGlobalModButtonClick,
  selectedModifierPath,
  renderChildren,
}) => {
  // Each row owns its own count, seeded from the item's current quantity.
  const [count, setCount] = useState(item.quantity || 0);

  // Keep count in sync when item.quantity changes externally (e.g. after Redux update).
  useEffect(() => {
    setCount(item.quantity || 0);
  }, [item.quantity]);
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedModArray = useMemo(
    () => items.map(i => i.modifierId),
    [items]
  );

  const isShowQty = modifiersHashTable[item?.modifierId]?.cardType === 0;
  const group = modifierGroupsHashTable[item.modifierGroupId];
  const isWeightGroup = group?.isWeight;
  const maxItems = group?.maximum;
  const maxQuantity = !maxItems ? Infinity : maxItems;

  const groupLevelTotal = levelGroupTotals[item.modifierGroupId] || 0;
  const totalWeight = levelGroupWeightTotals[item.modifierGroupId] || 0;

  const modLink = isWeightGroup
    ? group?.modifierLinks?.find(
        l => Number(l.modifierId) === Number(item.modifierId)
      )
    : null;
  const modWeight = modLink?.weight ?? 0;
  const isMaxReached = isWeightGroup
    ? modWeight
      ? totalWeight + modWeight > maxQuantity
      : totalWeight >= maxQuantity
    : groupLevelTotal >= maxQuantity;

  const showQtyButtons = level === activeLevel && isShowQty && maxQuantity !== 1;
  const isLastQty = item.quantity <= 1;
  const showPrice =
    !showQtyButtons &&
    auth?.serverAppFeatures?.displayPrice &&
    Number(item.price) > 0;

  const canEdit =
    modifierLevelData?.find(
      x => x.modifierGroupId === item.modifierGroupId
    ) &&
    modifiersHashTable?.[item?.modifierId]?.modifierGroupLinks?.length > 0;

  const noteKey = `${item.modifierId}_${item.modifierGroupId}`;

  const isGlobalModifier = menusList?.[menuIndex]?.globalModifierGroupIds?.includes(
    item.modifierGroupId
  );

  const showNoteButton =
    level === 0 &&
    !isNestedModifier &&
    modifierMemosEnabled() &&
    !isKiosk &&
    !isGlobalModifier;

  const showGlobalModButton =
    level === 0 &&
    !isNestedModifier &&
    !isKiosk &&
    !isTableTop &&
    isGlobalModFuncDisplay &&
    !isGlobalModifier;

  const hasActionButtons = showQtyButtons || canEdit || showNoteButton || showGlobalModButton;

  const handleToggle = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  };

  return (
    <div
      className={cn(
        level === 0 ? 'fs_15' : 'fs_12',
        'br-2',
        styles.modifierItemWrapper
      )}
      style={{
        backgroundColor: level === 0 ? '#EDEDED' : 'none',
        margin: level === 0 ? '4px 0px' : '4px 0px 0px',
        padding: level === 0 ? '4px' : '0px',
        marginLeft: level * 10,
      }}
    >
      {level === 0 ? (
        <label className="d-flex justify-content-between w-100 mb-0">
          <span>
            -{' '}
            {!showQtyButtons && item.quantity > 1 ? `${item.quantity}x ` : ''}
            {item.modifierName}
          </span>
          <div className="d-flex flex-column align-items-end">
            {showPrice && (
              <span className="fw-600">
                {LABEL().CURRENCY_SYMBOL}
                {Number(item.price ?? 0)?.toFixed(2)}
              </span>
            )}
            {hasActionButtons && (
              <TouchableButton
                type="button"
                className="border-0 d-flex align-items-center justify-content-center px-0"
                onClick={handleToggle}
                style={{
                  marginTop: showPrice ? '2px' : '0px',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                <ExpandMore height={'24'} width={24} fill={serverAppTheme?.iconcolor} />
              </TouchableButton>
            )}
          </div>
        </label>
      ) : (
        <div className="d-flex justify-content-between w-100 align-items-center">
          <span>
            -{' '}
            {!showQtyButtons && item.quantity > 1 ? `${item.quantity}x ` : ''}
            {item.modifierName}
          </span>
          <div className="d-flex flex-column align-items-end">
            {showPrice && (
              <span className="fw-400">
                {LABEL().CURRENCY_SYMBOL}
                {Number(item.price ?? 0)?.toFixed(2)}
              </span>
            )}
            {activeLevel > 0 && (showQtyButtons || canEdit) && (
              <TouchableButton
                type="button"
                className="border-0 d-flex align-items-center justify-content-center px-0"
                onClick={handleToggle}
                style={{
                  marginTop: showPrice ? '2px' : '0px',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                <ExpandMore width={24} height={24} fill={serverAppTheme?.iconcolor} />
              </TouchableButton>
            )}
          </div>
        </div>
      )}

      {noteText && (
        <div className="fs_12 pl-0 col-12 row mx-0">
          <span className="col-1 px-0">
            <NotesIcon />
          </span>
          <span
            className="col-11 pl-2 pr-0"
            style={{ color: serverAppTheme?.primary_text }}
          >
            {noteText}
          </span>
        </div>
      )}

      {level === 0 && isExpanded && (
        <div className="d-flex justify-content-between align-items-center pt-1">
          <div className="d-flex align-items-center">
            {showQtyButtons && (
              <div className="d-flex">
                {/* MINUS */}
                <TouchableButton
                  className={cn(
                    'align-items-center border-0 d-flex justify-content-around px-0',
                    styles.plusMinusIcon
                  )}
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    const {
                      countTemp,
                      selectedModifierPath: modPath,
                    } = onCountModifer({
                      data: item,
                      isInc: false,
                      disable: false,
                      count,
                      selectedModArray,
                      e,
                    });
                    setCount(countTemp);
                    if (nestedModifierArray.length === 0) {
                      pushModifierToCount(modPath, false);
                    } else {
                      pushModifierToCount(
                        getPath(nestedModifierArray, modPath),
                        false
                      );
                    }
                  }}
                  style={{ backgroundColor: serverAppTheme?.primaryBtn }}
                >
                  {isLastQty ? (
                    <ModifierDelete fill={serverAppTheme?.iconcolor}/>
                  ) : (
                    <ModifierMinus fill={serverAppTheme?.iconcolor} />
                  )}
                </TouchableButton>

                <span
                  className="px-2 d-flex align-items-center justify-content-center fw-600"
                  style={{ width: '22px' }}
                >
                  {isWeightGroup && modWeight > 0
                    ? item.quantity * modWeight
                    : item.quantity}
                </span>

                {/* PLUS */}
                <TouchableButton
                  disabled={isMaxReached}
                  className={cn(
                    'align-items-center border-0 d-flex justify-content-around px-0 mr-1',
                    styles.plusMinusIcon
                  )}
                  type="button"
                  onClick={e => {
                    if (isMaxReached) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const {
                      countTemp,
                      selectedModifierPath: modPath,
                    } = onCountModifer({
                      data: item,
                      isInc: true,
                      disable: false,
                      count,
                      selectedModArray,
                      e,
                    });
                    setCount(countTemp);
                    if (nestedModifierArray.length === 0) {
                      pushModifierToCount(modPath, true);
                    } else {
                      pushModifierToCount(
                        getPath(nestedModifierArray, modPath),
                        true
                      );
                    }
                  }}
                  style={{
                    backgroundColor: serverAppTheme?.primaryBtn,
                    opacity: isMaxReached ? 0.5 : 1,
                    cursor: isMaxReached ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ModifierPlus fill={serverAppTheme?.iconcolor} />
                </TouchableButton>
              </div>
            )}
          </div>
          <div
            className="d-flex align-items-center flex-grow-1"
            style={{ marginBottom : '3px' }}
          >
            {showNoteButton && (
              <TouchableButton
                type="button"
                className="align-items-center border-0 p-1 d-flex justify-content-around  br-2 flex-shrink-0"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onNoteOpen({
                    modifierId: item.modifierId,
                    modifierGroupId: item.modifierGroupId,
                  });
                }}
                style={{
                  backgroundColor: serverAppTheme?.primaryBtn,
                  width: '20px',
                  height: '20px',
                }}
                rippleEffect={true}
              >
                <ModifierNoteLogo
                  fill={serverAppTheme?.seat_box_text}
                  height={14}
                  width={14}
                />
              </TouchableButton>
            )}
            {showGlobalModButton && (
              <TouchableButton
                type="button"
                className="align-items-center border-0 d-flex justify-content-around px-0 br-2 flex-shrink-0"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleGlobalModButtonClick(
                    item.modifierId,
                    item.modifierGroupId
                  );
                }}
                style={{
                  backgroundColor:
                    selectedModifierPath ===
                    `${item.modifierId}-${item.modifierGroupId}`
                      ? serverAppTheme?.secondaryBtn
                      : serverAppTheme?.primaryBtn,
                  width: '38px',
                  height: '20px',
                  marginLeft: '4px',
                }}
                rippleEffect={true}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 11 11"
                  fill="#ffffff"
                >
                  <path d="M1.5 9.5C1.225 9.5 0.989583 9.40208 0.79375 9.20625C0.597917 9.01042 0.5 8.775 0.5 8.5V1.5C0.5 1.225 0.597917 0.989583 0.79375 0.79375C0.989583 0.597917 1.225 0.5 1.5 0.5H8.5C8.775 0.5 9.01042 0.597917 9.20625 0.79375C9.40208 0.989583 9.5 1.225 9.5 1.5V5.675C9.34167 5.61667 9.17917 5.57292 9.0125 5.54375C8.84583 5.51458 8.675 5.5 8.5 5.5V1.5H1.5V8.5H5.5C5.5 8.675 5.51458 8.84583 5.54375 9.0125C5.57292 9.17917 5.61667 9.34167 5.675 9.5H1.5ZM8.5 10.5L7.8 9.8L8.5875 9H6.5V8H8.5875L7.8 7.2L8.5 6.5L10.5 8.5L8.5 10.5ZM4.5 7.5H5.5V5.5H7.5V4.5H5.5V2.5H4.5V4.5H2.5V5.5H4.5V7.5Z" />
                </svg>
              </TouchableButton>
            )}
            {canEdit && (
              <TouchableDiv
                role="presentation"
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  onModifierEdit(item);
                }}
                rippleEffect={true}
                className="flex-grow-1"
              >
                <div
                  className={cn(
                    'd-flex justify-content-center align-items-center',
                    styles.modifyBtn
                  )}
                  style={{
                    backgroundColor: serverAppTheme?.primaryBtn,
                    color: serverAppTheme?.seat_box_text,
                  }}
                >
                  Modify
                </div>
              </TouchableDiv>
            )}
          </div>
        </div>
      )}

      {level > 0 && activeLevel > 0 && isExpanded && (showQtyButtons || canEdit) && (
        <div className="d-flex justify-content-between mx-0">
          {showQtyButtons ? (
            <div className="d-flex justify-content-between flex-grow-1 pt-1">
              <div className="d-flex">
                {/* MINUS */}
                <TouchableButton
                  className={cn(
                    'align-items-center border-0 d-flex justify-content-around px-0',
                    styles.plusMinusIcon
                  )}
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    const {
                      countTemp,
                      selectedModifierPath: modPath,
                    } = onCountModifer({
                      data: item,
                      isInc: false,
                      disable: false,
                      count,
                      selectedModArray,
                      e,
                    });
                    setCount(countTemp);
                    if (nestedModifierArray.length === 0) {
                      pushModifierToCount(modPath, false);
                    } else {
                      pushModifierToCount(
                        getPath(nestedModifierArray, modPath),
                        false
                      );
                    }
                  }}
                  style={{ backgroundColor: serverAppTheme?.primaryBtn }}
                >
                  {isLastQty ? (
                    <ModifierDelete fill={serverAppTheme?.iconcolor} />
                  ) : (
                    <ModifierMinus fill={serverAppTheme?.iconcolor} />
                  )}
                </TouchableButton>

                <span
                  className="px-2 d-flex align-items-center justify-content-center fw-600"
                  style={{ width: '22px' }}
                >
                  {isWeightGroup && modWeight > 0
                    ? item.quantity * modWeight
                    : item.quantity}
                </span>

                {/* PLUS */}
                <TouchableButton
                  disabled={isMaxReached}
                  className={cn(
                    'align-items-center border-0 d-flex justify-content-around px-0 mr-1',
                    styles.plusMinusIcon
                  )}
                  type="button"
                  onClick={e => {
                    if (isMaxReached) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const {
                      countTemp,
                      selectedModifierPath: modPath,
                    } = onCountModifer({
                      data: item,
                      isInc: true,
                      disable: false,
                      count,
                      selectedModArray,
                      e,
                    });
                    setCount(countTemp);
                    if (nestedModifierArray.length === 0) {
                      pushModifierToCount(modPath, true);
                    } else {
                      pushModifierToCount(
                        getPath(nestedModifierArray, modPath),
                        true
                      );
                    }
                  }}
                  style={{
                    backgroundColor: serverAppTheme?.primaryBtn,
                    opacity: isMaxReached ? 0.5 : 1,
                    cursor: isMaxReached ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ModifierPlus fill={serverAppTheme?.iconcolor} />
                </TouchableButton>
              </div>
              <div className="d-flex align-items-center flex-grow-1">
                {canEdit && (
                  <TouchableDiv
                    role="presentation"
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      onModifierEdit(item);
                    }}
                    rippleEffect={true}
                    className="flex-grow-1"
                  >
                    <div
                      className={cn(
                        'd-flex justify-content-center align-items-center',
                        styles.modifyBtn
                      )}
                      style={{
                        backgroundColor: serverAppTheme?.primaryBtn,
                        color: serverAppTheme?.seat_box_text,
                      }}
                    >
                      Modify
                    </div>
                  </TouchableDiv>
                )}
              </div>
            </div>
          ) : (
            canEdit && (
              <div className="d-flex align-items-center justify-content-end w-100 pt-1">
                <TouchableDiv
                  role="presentation"
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onModifierEdit(item);
                  }}
                  rippleEffect={true}
                  className="flex-grow-1"
                >
                  <div
                    className={cn(
                      'd-flex justify-content-center align-items-center',
                      styles.modifyBtn
                    )}
                    style={{
                      backgroundColor: serverAppTheme?.primaryBtn,
                      color: serverAppTheme?.seat_box_text,
                    }}
                  >
                    Modify
                  </div>
                </TouchableDiv>
              </div>
            )
          )}
        </div>
      )}

      {item.modifiers?.length > 0 && renderChildren(item.modifiers, level + 1)}
    </div>
  );
});

ModifierRowItem.displayName = 'ModifierRowItem';

export default ModifierRowItem;
